import React, { useEffect, useRef, useState, ReactNode, HTMLAttributes } from 'react';

type AnimationType =
    | 'fade-up'
    | 'fade-down'
    | 'fade-left'
    | 'fade-right'
    | 'scale-up'
    | 'scale-down'
    | 'rotate-in'
    | 'blur-in'
    | 'flip'
    | 'bounce-in'
    | 'slide-bounce'
    | 'card'
    | 'image-reveal'
    | 'text-reveal';

interface AnimatedSectionProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
    animation?: AnimationType;
    delay?: number; // delay in ms (100, 200, 300, etc.)
    duration?: 'fast' | 'normal' | 'slow';
    threshold?: number;
    triggerOnce?: boolean;
    as?: 'div' | 'section' | 'article' | 'aside' | 'header' | 'footer' | 'main' | 'nav' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    className?: string;
}

const AnimatedSection: React.FC<AnimatedSectionProps> = ({
    children,
    animation = 'fade-up',
    delay,
    duration,
    threshold = 0.1,
    triggerOnce = true,
    as: Component = 'div',
    className = '',
    ...props
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (triggerOnce) {
                        observer.unobserve(element);
                    }
                } else if (!triggerOnce) {
                    setIsVisible(false);
                }
            },
            {
                threshold,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        observer.observe(element);

        return () => {
            observer.unobserve(element);
        };
    }, [threshold, triggerOnce]);

    const animationClass = `animate-${animation}`;
    const delayClass = delay ? `delay-${delay}` : '';
    const durationClass = duration ? `duration-${duration}` : '';
    const visibleClass = isVisible ? 'visible' : '';

    const combinedClassName = [
        animationClass,
        delayClass,
        durationClass,
        visibleClass,
        className
    ].filter(Boolean).join(' ');

    const DynamicComponent = Component as any;

    return (
        <DynamicComponent
            ref={ref}
            className={combinedClassName}
            {...props}
        >
            {children}
        </DynamicComponent>
    );
};

export default AnimatedSection;
